import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Logo from "./components/Logo";
import { lazy, Suspense } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Clock, LogOut } from "lucide-react";

// Lazy load pages
const Layout = lazy(() => import("./components/Layout"));
const InstallPWA = lazy(() => import("./components/InstallPWA"));
const Login = lazy(() => import("./pages/Login"));
const RegisterCompany = lazy(() => import("./pages/RegisterCompany"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Coches = lazy(() => import("./pages/Coches"));
const Conductores = lazy(() => import("./pages/Conductores"));
const Recepcion = lazy(() => import("./pages/Recepcion"));
const GastosCoche = lazy(() => import("./pages/GastosCoche"));
const Banko = lazy(() => import("./pages/Banko"));
const Reportes = lazy(() => import("./pages/Reportes"));
const Configuracion = lazy(() => import("./pages/Configuracion"));
const Usuarios = lazy(() => import("./pages/Usuarios"));
const Companies = lazy(() => import("./pages/Companies"));
const MigrationReview = lazy(() => import("./pages/MigrationReview"));
const PerfilConductor = lazy(() => import("./pages/PerfilConductor"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center p-6">
    <div className="text-center space-y-3">
      <img src="/icons/icon-192x192.png" alt="EVO TAXI" className="w-16 h-16 rounded-2xl mx-auto shadow-sm" />
      <p className="text-xl font-black">EVO TAXI</p>
      <p className="text-sm text-slate-500">Abriendo módulo...</p>
    </div>
  </div>
);

const AppLoading = () => (
  <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center p-6">
    <div className="text-center space-y-3">
      <img src="/icons/icon-192x192.png" alt="EVO TAXI" className="w-16 h-16 rounded-2xl mx-auto shadow-sm" />
      <p className="text-xl font-black">EVO TAXI</p>
      <p className="text-sm text-slate-500">Preparando acceso...</p>
    </div>
  </div>
);

// Pending Approval Screen
const PendingApproval = ({ user, onLogout }) => {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-card border-border">
        <CardHeader className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-lg bg-white/95 mb-4 mx-auto p-2">
            <Logo className="w-full h-full" variant="light" />
          </div>
          <CardTitle className="font-heading text-xl">Cuenta Pendiente de Aprobación</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
            <Clock className="w-8 h-8 text-primary" />
          </div>
          <p className="text-muted-foreground">
            Tu cuenta <strong>{user?.email || user?.username}</strong> está pendiente de aprobación por un administrador.
          </p>
          <p className="text-sm text-muted-foreground">
            Recibirás acceso una vez que un administrador apruebe tu solicitud.
          </p>
          <Button
            variant="outline"
            onClick={onLogout}
            className="mt-4"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// AuthCallback removed - Emergent dependency removed
/*
const AuthCallback = () => {
  // ... (removed)
};
*/

const ProtectedRoute = ({ children, roles = [] }) => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return <AppLoading />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Check if user is approved
  if (user && !user.is_approved) {
    return <PendingApproval user={user} onLogout={() => { logout(); navigate('/'); }} />;
  }

  // Check role access
  if (roles.length > 0 && user) {
    const platformOnly = roles.every((role) => ["SUPER_ADMIN", "AGENTE_COBROS"].includes(role));
    if (platformOnly && !["SUPER_ADMIN", "AGENTE_COBROS"].includes(user.role)) {
      return <Navigate to="/dashboard" replace />;
    }
    if (user.role === "SUPER_ADMIN" || user.role === "AGENTE_COBROS") {
      return roles.includes(user.role)
        ? <Layout>{children}</Layout>
        : <Navigate to="/platform" replace />;
    }
    // Admin Director has access to everything (legacy is_admin support too)
    if (user.role === "ADMIN_DIRECTOR" || user.is_admin) {
      return <Layout>{children}</Layout>;
    }
    
    if (!roles.includes(user.role)) {
      // Redirect based on role
      if (user.role === "CHOFER") return <Navigate to="/perfil" replace />;
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { token, user, loading } = useAuth();

  if (loading) {
    return <AppLoading />;
  }

  if ((token || user) && user?.is_approved) {
    if (user.role === "SUPER_ADMIN" || user.role === "AGENTE_COBROS") return <Navigate to="/platform" replace />;
    if (user.role === "CHOFER") return <Navigate to="/perfil" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const LocalReviewRoute = ({ children }) => {
  const isLocalPreview = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  if (!isLocalPreview) return <Navigate to="/" replace />;
  return children;
};

function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          path="/"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register-company"
          element={
            <PublicRoute>
              <RegisterCompany />
            </PublicRoute>
          }
        />
        <Route
          path="/revision-migracion"
          element={
            <LocalReviewRoute>
              <MigrationReview />
            </LocalReviewRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute roles={["ADMIN_DIRECTOR", "GESTOR"]}>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coches"
          element={
            <ProtectedRoute roles={["ADMIN_DIRECTOR", "GESTOR"]}>
              <Coches />
            </ProtectedRoute>
          }
        />
        <Route
          path="/conductores"
          element={
            <ProtectedRoute roles={["ADMIN_DIRECTOR", "GESTOR"]}>
              <Conductores />
            </ProtectedRoute>
          }
        />
        <Route
          path="/registro"
          element={
            <ProtectedRoute roles={["ADMIN_DIRECTOR", "GESTOR"]}>
              <Recepcion />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gastos"
          element={
            <ProtectedRoute roles={["ADMIN_DIRECTOR", "GESTOR"]}>
              <GastosCoche />
            </ProtectedRoute>
          }
        />
        <Route
          path="/banko"
          element={
            <ProtectedRoute roles={["ADMIN_DIRECTOR", "GESTOR"]}>
              <Banko />
            </ProtectedRoute>
          }
        />
        <Route
          path="/banko/caja"
          element={
            <ProtectedRoute roles={["ADMIN_DIRECTOR", "GESTOR"]}>
              <Banko />
            </ProtectedRoute>
          }
        />
        <Route
          path="/banko/banco"
          element={
            <ProtectedRoute roles={["ADMIN_DIRECTOR", "GESTOR"]}>
              <Banko />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reportes"
          element={
            <ProtectedRoute roles={["ADMIN_DIRECTOR", "GESTOR"]}>
              <Reportes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/configuracion"
          element={
            <ProtectedRoute roles={["ADMIN_DIRECTOR", "GESTOR"]}>
              <Configuracion />
            </ProtectedRoute>
          }
        />
        <Route
          path="/usuarios"
          element={
            <ProtectedRoute roles={["ADMIN_DIRECTOR"]}>
              <Usuarios />
            </ProtectedRoute>
          }
        />
        <Route
          path="/companies"
          element={
            <ProtectedRoute roles={["SUPER_ADMIN", "AGENTE_COBROS"]}>
              <Companies />
            </ProtectedRoute>
          }
        />
        {[
          '/platform',
          '/platform/companies',
          '/platform/plans',
          '/platform/payments',
          '/platform/platform-users',
          '/platform/audit',
          '/platform/config'
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "AGENTE_COBROS"]}>
                <Companies />
              </ProtectedRoute>
            }
          />
        ))}
        {[
          '/mobility/clients',
          '/mobility/rides',
          '/mobility/drivers-online',
          '/mobility/fares',
          '/mobility/zones',
          '/jobs/offers',
          '/jobs/candidates',
          '/jobs/applications',
          '/jobs/chats',
          '/safety/sos',
          '/safety/shared-rides',
          '/safety/verifications',
          '/safety/blocks',
          '/platform/commissions',
          '/platform/notifications',
          '/platform/settings'
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute roles={["SUPER_ADMIN"]}>
                <ComingSoon />
              </ProtectedRoute>
            }
          />
        ))}
        {[
          '/empresa/empleo',
          '/empresa/viajes',
          '/empresa/seguridad'
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute roles={["ADMIN_DIRECTOR", "GESTOR"]}>
                <ComingSoon />
              </ProtectedRoute>
            }
          />
        ))}
        <Route
          path="/perfil"
          element={
            <ProtectedRoute roles={["CHOFER", "ADMIN_DIRECTOR", "GESTOR"]}>
              <PerfilConductor />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app-mobile-container">
          <div className="App">
            <AppRouter />
            <Suspense fallback={null}>
              <InstallPWA />
            </Suspense>
          </div>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
